import React from 'react'
import { Map, Set, List } from 'immutable'
import _map from 'lodash/map'
import _size from 'lodash/size'
import _isArray from 'lodash/isArray'
import {
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PERSON,
  TYPE_CONTENT_TAG,
  TYPE_CONTENT_SERIES_GUIDE,
  TYPE_CONTENT_EPISODE_GUIDE,
  isGuideType,
} from 'services/content-type'
import {
  CONTENT_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
  CONTENT_MEDIA_REFERENCE_TYPE_MEDIA_STREAM,
  CONTENT_MEDIA_REFERENCE_TYPE_SUBTITLE,
  upgradeToWatchCheck,
  featureAllowedWithUserSubscription,
  getFeaturedContent,
  getFeatureMedia,
  isEpisode,
} from 'services/content'
import { URL_GAIA_PLUS } from 'services/url/constants'
import {
  formatSeriesEpisodeTotals,
  formatDuration,
} from 'theme/web-app'
import Link from 'components/Link'
import { TARGET_BLANK } from 'components/Link/constants'
import Vote, { SIZE_SMALL as VOTE_SIZE_SMALL } from 'components/Vote'
import WatchV2 from 'components/Watch.v2'
import PlaylistAddRemove from 'components/PlaylistAddRemove'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import { TYPES } from 'components/Button'
import TextSeasonEpisode from 'components/TextSeasonEpisode'
import TileV2Tags from '../Tile.v2Tags'
import TileV2Guests from '../Tile.v2Guests'
import { TILE_V2_TAGS_SIZE_SMALL } from '../constants'
import {
  handleTileV2OnClick,
  getTileV2ElementClassName,
} from '../utils'

/**
 * The title rendered as a link
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {String} props.url - The url of the content
 * @param {Function} props.onClick - The onclick function
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} - The content title as a link
 */
function TitleLink (props) {
  const {
    title,
    url,
    onClick,
    contentType,
  } = props
  const isContentTypeProduct = contentType === TYPE_CONTENT_PRODUCT

  if (!title || !url) {
    return null
  }

  return (
    <Link
      to={url}
      className={`tile-v2__title tile-v2__title--${contentType}`}
      onClick={onClick}
      target={isContentTypeProduct ? TARGET_BLANK : null}
      directLink={isContentTypeProduct}
    >
      {title}
    </Link>
  )
}

/**
 * Renders the season and episode numbers
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @param {Boolean} props.isGuide - Whether or not the content is a guide
 * @returns {String|null} - If seasonNumber and episodeNumber are null, return null.
 *  Otherwise return the formatted season and episode numbers
 */
function SeasonEpisodeNumbers (props) {
  const { seasonNumber, episodeNumber, isGuide } = props

  return (
    <TextSeasonEpisode
      className={['tile-v2__season-episode-numbers']}
      season={seasonNumber}
      episode={episodeNumber}
      isGuide={isGuide}
    />
  )
}

/**
 * Series season episode in the "Series Title: S1:E1" format
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {JSX.Element|null} Series title combined with the season and episode number
 */
function SeriesSeasonEpisode (props) {
  const {
    title,
    seasonNumber,
    episodeNumber,
    isGuide,
  } = props

  if (!title || !seasonNumber || !episodeNumber) {
    return null
  }

  return (
    <div className="tile-v2__series-season-episode">
      <SeasonEpisodeNumbers
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
      />
      <span className="tile-v2__series-title">
        {`${title} `}
      </span>
    </div>
  )
}

/**
 * Upgrade to watch button for Gaia+
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Component for upgrade to watch CTA
 */
function UpgradeToWatchButton (props) {
  const { staticText } = props
  return (
    <Link
      className="tile-v2__upgrade-btn"
      to={URL_GAIA_PLUS}
    >
      <span className="tile-v2__upgrade-icon">
        <IconV2 type={ICON_TYPES.PADLOCK} />
      </span>
      <span className="tile-v2__upgrade-text">
        {staticText.get('upgradeWatch')}
      </span>
    </Link>
  )
}

/**
 * Feature not allowed with subscription button
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @param {String} props.path - The content url
 * @returns {JSX.Element} Component for feature not allowed with subscription CTA
 */
function FeatureNotAllowedButton (props) {
  const { staticText, path } = props
  return (
    <Link
      className="tile-v2__feature-not-allowed"
      to={path}
    >
      <span className="tile-v2__feature-not-allowed-icon">
        <IconV2 type={ICON_TYPES.PLAY} />
      </span>
      <span className="tile-v2__feature-not-allowed-text">
        {staticText.get('watchNow')}
      </span>
    </Link>
  )
}

/**
 * Metadata controls such as the watch button, add to playlist, etc.
 * @param {Object} props - The props
 * @param {JSX.Element} props.watch - The jsx elements for the watch button
 * @param {JSX.Element} props.playlist - The jsx elements for the add to playlist button
 * @returns {JSX.Element} The metadata action controls
 */
function MetadataControls (props) {
  const { watch, playlist } = props

  return (
    <div className="tile-v2__metadata-controls">
      <div className="tile-v2__watch">
        {watch}
      </div>
      <div className="tile-v2__control-buttons">
        {playlist}
      </div>
    </div>
  )
}

/**
 * The series controls, such as play button, add to playlist, info, etc.
 * @param {Object} props - The props
 * @param {Map} props.upstreamContext - The upstream context
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isLoggedIn - Logged in status for the user
 * @param {Map} props.data - The data for the series tile
 * @param {List} props.subscriptions - The user's subscriptions - entitlements
 * @returns {JSX.Element} - The rendered controls for a series tile
 */
function SeriesControls (props) {
  const {
    upstreamContext = Map(),
    staticText = Map(),
    isLoggedIn,
    data = Map(),
    subscriptions,
    rowPlacement = null,
    index = null,
    adminTitle,
  } = props
  const featuredEpisode = getFeaturedContent(props)
  const feature = getFeatureMedia(props)
  const featuredEpisodeTitle = featuredEpisode.get('title', '')
  const featuredEpisodeId = featuredEpisode.get('contentId', -1)
  const featuredEpisodeWatchText = staticText.get('watchNow')
  const featuredEpisodePath = featuredEpisode.get('path', '') || data.get('url')
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFreeAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])
  const isFeatureAllowed = featureAllowedWithUserSubscription(props)
  const upgradeToWatch = upgradeToWatchCheck({
    feature,
    subscriptions,
    isFeatureAllowed,
    isFreeAllowed,
  })

  let seriesWatch = (
    <WatchV2
      iconType={ICON_TYPES.CIRCULAR_PLAY}
      upstreamContext={upstreamContext}
      onClickWatch={(e) => handleTileV2OnClick(e, props, true)}
      buttonType={TYPES.ICON}
      className="tile-v2__watch-button"
      text={featuredEpisodeWatchText}
      url={featuredEpisodePath}
      watchAccess={watchAccess}
      isFree={isFreeAllowed}
    />
  )

  if (upgradeToWatch) {
    seriesWatch = (
      <UpgradeToWatchButton staticText={staticText} />
    )
  }

  if (!isFeatureAllowed && !isFreeAllowed && !upgradeToWatch) {
    seriesWatch = (
      <FeatureNotAllowedButton staticText={staticText} path={featuredEpisodePath} />
    )
  }

  const seriesAddPlaylist = (
    <>
      {
        featuredEpisodeId !== -1 && isLoggedIn
          ? <div className="tile-v2__playlist-add-remove" title={staticText.get('addRemove')}>
              <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={featuredEpisodeId}
                contentTitle={featuredEpisodeTitle}
                circlularIcon
                notext
                ga4EventData={{
                  content_id: featuredEpisodeId,
                  content_type: featuredEpisode.get('contentType'),
                  content_name: featuredEpisodeTitle,
                  media_id: feature.get('mediaId'),
                  media_language: data.get('languageFullText'),
                  is_featured_media: true,
                  parent_series_name: data.get('title'),
                  parent_series_id: data.get('contentId'),
                  ui_element: 'Tile',
                  row_placement: rowPlacement,
                  tile_number: index,
                  row_name: adminTitle,
                }}
              />
            </div>
          : null
      }
    </>
  )

  return (
    <MetadataControls
      watch={seriesWatch}
      playlist={seriesAddPlaylist}
    />
  )
}

/**
 * The video and episode controls, such as play button, add to playlist, info, etc.
 * @param {Object} props - The props
 * @param {Map} props.upstreamContext - The upstream context
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isLoggedIn - Logged in status for the user
 * @param {Map} props.data - The data for the video or episode tile
 * @param {List} props.subscriptions - The user's subscriptions - entitlements
 * @returns {JSX.Element} - The rendered controls for a video or episode tile
 */
function VideoEpisodeControls (props) {
  const {
    upstreamContext = Map(),
    staticText = Map(),
    isLoggedIn,
    data = Map(),
    subscriptions,
    rowPlacement = null,
    index = null,
    adminTitle,
  } = props
  const videoEpisodeTitle = data.get('title', '')
  const videoEpisodeId = data.get('contentId', -1)
  const videoEpisodeWatchText = staticText.get('watchNow')
  const videoEpisodePath = data.get('path', '')
  const feature = getFeatureMedia(props)
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFreeAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])
  const isFeatureAllowed = featureAllowedWithUserSubscription(props)
  const upgradeToWatch = upgradeToWatchCheck({
    feature,
    subscriptions,
    isFeatureAllowed,
    isFreeAllowed,
  })
  let videoEpisodeWatch = (
    <WatchV2
      iconType={ICON_TYPES.CIRCULAR_PLAY}
      upstreamContext={upstreamContext}
      onClickWatch={(e) => handleTileV2OnClick(e, props, true)}
      buttonType={TYPES.ICON}
      className="tile-v2__watch-button"
      text={videoEpisodeWatchText}
      url={videoEpisodePath}
      watchAccess={watchAccess}
      isFree={isFreeAllowed}
    />
  )

  if (upgradeToWatch) {
    videoEpisodeWatch = (
      <UpgradeToWatchButton staticText={staticText} />
    )
  }

  if (!isFeatureAllowed && !isFreeAllowed && !upgradeToWatch) {
    videoEpisodeWatch = (
      <FeatureNotAllowedButton staticText={staticText} path={videoEpisodePath} />
    )
  }

  const videoEpisodeAddPlaylist = (
    <>
      {
        isLoggedIn
          ? <div className="tile-v2__playlist-add-remove" title={staticText.get('addRemove')}>
              <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={videoEpisodeId}
                contentTitle={videoEpisodeTitle}
                circlularIcon
                notext
                ga4EventData={{
                  content_id: videoEpisodeId,
                  content_type: data.get('contentType'),
                  content_name: videoEpisodeTitle,
                  media_id: feature.get('mediaId'),
                  media_language: data.get('languageFullText'),
                  is_featured_media: true,
                  parent_series_name: data.get('seriesTitle'),
                  parent_series_id: data.get('seriesId'),
                  ui_element: 'Tile',
                  row_placement: rowPlacement,
                  tile_number: index,
                  row_name: adminTitle,
                }}
              />
            </div>
          : null
      }
    </>
  )

  return (
    <MetadataControls
      watch={videoEpisodeWatch}
      playlist={videoEpisodeAddPlaylist}
    />
  )
}

/**
 * Audio and subtitle icons
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {List} props.userLanguages - List of the user's languages
 *  legacy source (i.e., non member home tile requests)
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} The audio and subtitle icons
 */
function AudioSubtitleIcons (props) {
  const {
    data = Map(),
    userLanguages,
    contentType,
  } = props
  const mediaLang = data.get('primaryLanguage')
  const langIconsNeeded = !userLanguages.includes(mediaLang)
  const feature = getFeatureMedia(props)
  const mediaAssets = List.isList(feature.get('mediaAssets')) ? feature.get('mediaAssets', List()) : List()
  const mediaStreams = mediaAssets.filter((a = Map()) => {
    return a.get('mediaReferenceType') === CONTENT_MEDIA_REFERENCE_TYPE_MEDIA_STREAM
  }) || List()
  const subtitles = mediaAssets.filter((a = Map()) => {
    return a.get('mediaReferenceType') === CONTENT_MEDIA_REFERENCE_TYPE_SUBTITLE
  }) || List()
  const captions = mediaAssets.filter((a = Map()) => {
    return a.get('mediaReferenceType') === CONTENT_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION
  }) || List()
  const availableLangs = mediaStreams.map((i) => {
    return i.get('language')
  }, List())
  const availableSubtitles = subtitles.map((i) => {
    return i.get('language')
  }, List())
  const availableCaptions = captions.map((i) => {
    return i.get('language')
  }, List())

  if (contentType !== TYPE_CONTENT_SERIES && contentType !== TYPE_CONTENT_VIDEO) {
    return null
  }

  // if we have no media assets, bail
  if (!mediaAssets.size) {
    return null
  }

  // if we don't have the proper data, bail
  if (
    (List.isList(availableLangs) && availableLangs.size === 0)
      || (List.isList(availableCaptions) && availableCaptions.size === 0
        && List.isList(availableSubtitles) && availableSubtitles.size === 0)
  ) {
    return null
  }

  const availableTextTracks = Set([
    ...availableSubtitles,
    ...availableCaptions,
  ])
  const audioOfInterest = langIconsNeeded
    ? availableLangs.some((al) => userLanguages.includes(al))
    : undefined
  const textTrackOfInterest = langIconsNeeded
    ? availableTextTracks.some((att) => userLanguages.includes(att))
    : undefined
  const icons = []

  if (audioOfInterest) {
    icons.push(<IconV2 type={ICON_TYPES.HEADPHONES} key={'headphones'} />)
  }

  if (textTrackOfInterest) {
    icons.push(<IconV2 type={ICON_TYPES.SWITCH_LANGUAGE_OUTLINE} key={'switch-language'} />)
  }

  return icons.length ? icons : null
}

/**
 * The vote/thumbs up component
 * @param {Object} props - The props
 * @param {Map} - The content data
 * @param {Map} - The user's auth
 * @param {Map} - The featured episode
 * @param {Boolean} - If the featured episode is in progress or not
 * @returns {JSX.Element} - The vote/thumbs up component
 */
function VoteReadOnly (props) {
  const {
    data = Map(),
    auth = Map(),
    featuredEpisode = Map(),
    featuredEpisodeInProgress = false,
  } = props
  const voteUp = data.getIn(['vote', 'upVote'], null)
  const voteDown = data.getIn(['vote', 'downVote'], null)
  const featuredEpisodeVoteUp = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['vote', 'upVote'], 0) : 0
  const featuredEpisodeVoteDown = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['vote', 'downVote'], 0) : 0
  const featuredEpisodeContentId = Map.isMap(featuredEpisode) ? featuredEpisode.get('contentId', -1) : -1
  const contentId = data.get('contentId')

  return (
    <Vote
      className="vote--right-align vote--small"
      size={VOTE_SIZE_SMALL}
      voteDown={featuredEpisodeInProgress ? featuredEpisodeVoteDown : voteDown}
      vote={featuredEpisodeInProgress ? featuredEpisodeVoteUp : voteUp}
      voteId={featuredEpisodeInProgress ? featuredEpisodeContentId : contentId}
      auth={auth}
      upOnly
    />
  )
}

/**
 * Metadata footer, the very bottom of a tile
 * @param {Object} props - The props
 * @param {Array} props.children - The child elements of the component
 * @returns {JSX.Element|null} Wrapped metadata footer elements
 */
function MetadataFooter (props) {
  const { children } = props

  if (!children || !_isArray(children)) {
    return null
  }

  const childrenLength = _size(children)

  return (
    <div className="tile-v2__metadata-footer">
      <div className="tile-v2__metadata-footer-left">
        {
          _map(children, (item, index) => {
            const itemKey = `metadata-item-${index}`

            return (
              <span
                key={itemKey}
                className={`tile-v2__metadata-item${index === childrenLength - 1 ? ' tile-v2__metadata-item--last' : ''}`}
              >
                {item}
              </span>
            )
          })
        }
      </div>
    </div>
  )
}

/**
 * The series metadata
 * @param {Object} props - The props
 * @param {String} props.contentType - The content type
 * @param {Map} props.data - The series data
 * @param {Map} props.staticText - The static text
 * @param {Map} props.userNodeInfo - The userNodeInfo for the series, including featured episode
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 *  legacy source (i.e., non member home tile requests)
 * @returns {JSX.Element} - The series metadata
 */
function SeriesMetadata (props) {
  const {
    contentType,
    data,
    staticText,
  } = props
  const title = data.get('title', '')
  const url = data.get('path', '')
  const featuredEpisode = getFeaturedContent(props)
  const featuredEpisodeInProgress = featuredEpisode.getIn(['featureMedia', 'userMediaPosition', 'position'], 0) > 0
  const featuredEpisodeTitle = featuredEpisode.get('title', '')
  const featuredEpisodeUrl = featuredEpisode.get('path', '')
  const seasonTotal = data.get('seasonsCount')
  const seasonText = staticText.get('season')
  const seasonsText = staticText.get('seasons')
  const lessonsTotal = data.get('episodesCount')
  const lessonText = staticText.get('lesson')
  const lessonsText = staticText.get('lessons')
  const featuredEpisodeDuration = featuredEpisode.getIn(['featureMedia', 'duration'], 0)
  const featuredEpisodeNumber = featuredEpisode.get('episodeNumber', 0)
  const featuredEpisodeSeasonNumber = featuredEpisode.get('seasonNumber', 0)
  const isGuide = isGuideType(data)

  return (
    <>
      <SeriesControls {...props} />
      <TitleLink
        title={featuredEpisodeInProgress ? featuredEpisodeTitle : title}
        url={featuredEpisodeInProgress ? featuredEpisodeUrl : url}
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Title')}
        contentType={contentType}
      />
      <TileV2Guests
        {...props}
        featuredEpisode={featuredEpisode}
        featuredEpisodeInProgress={featuredEpisodeInProgress}
      />
      {
        featuredEpisodeInProgress
          ? <SeriesSeasonEpisode
              title={title}
              seasonNumber={featuredEpisodeSeasonNumber}
              episodeNumber={featuredEpisodeNumber}
              isGuide={isGuide}
            />
          : null
      }
      <MetadataFooter>
        {
          featuredEpisodeInProgress
            ? formatDuration(featuredEpisodeDuration)
            : formatSeriesEpisodeTotals(
              isGuide ? lessonsTotal : seasonTotal,
              isGuide ? lessonText : seasonText,
              isGuide ? lessonsText : seasonsText,
            )
        }
        <AudioSubtitleIcons {...props} />
        <VoteReadOnly
          {...props}
          featuredEpisode={featuredEpisode}
          featuredEpisodeInProgress={featuredEpisodeInProgress}
        />
      </MetadataFooter>
      <TileV2Tags {...props} size={TILE_V2_TAGS_SIZE_SMALL} />
    </>
  )
}

/**
 * The metadata for videos and episodes
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} - The video or episode metadata
 */
function VideoEpisodeMetadata (props) {
  const {
    contentType,
    data,
  } = props
  const duration = data.getIn(['featureMedia', 'duration'], 0)
  const title = data.get('title', '')
  const url = data.get('path', '')
  const seasonNumber = data.get('seasonNumber', 0)
  const episodeNumber = data.get('episodeNumber', 0)
  const seriesTitle = data.get('seriesTitle', '')
  const isGuide = isGuideType(data)

  return (
    <>
      <VideoEpisodeControls {...props} />
      <TitleLink
        title={title}
        url={url}
        onClick={(e) => handleTileV2OnClick(e, props)}
        contentType={contentType}
      />
      <TileV2Guests {...props} />
      {
        isEpisode(props) && seasonNumber && episodeNumber
          ? <SeriesSeasonEpisode
              title={seriesTitle}
              seasonNumber={seasonNumber}
              episodeNumber={episodeNumber}
              isGuide={isGuide}
            />
          : null
      }
      <MetadataFooter>
        {formatDuration(duration)}
        <AudioSubtitleIcons {...props} />
        <VoteReadOnly {...props} />
      </MetadataFooter>
      <TileV2Tags {...props} size={TILE_V2_TAGS_SIZE_SMALL} />
    </>
  )
}

/**
 * A wrapper for the metadata
 * @param {Object} props - The props
 * @returns {JSX.Element} - The wrapped metadata children
 */
function MetadataWrapper (props) {
  const {
    children,
    hovered,
    display,
    contentType,
  } = props

  return (
    <div className={
      getTileV2ElementClassName({
        hovered,
        contentType,
        display,
        baseClassName: 'tile-v2__metadata',
      })}
    >
      {children}
    </div>
  )
}

/**
 * Renders metadata based on the content type.
 * @param {Object} props - The properties object.
 * @param {String} props.contentType - The type of content.
 * @param {Map} props.data - The content data
 * @returns {JSX.Element|null} - The rendered metadata or null.
*/
function Metadata (props) {
  const {
    contentType,
    data,
  } = props
  const title = data.get('title', '')
  const url = data.get('path', '')
  const ctaProductUrl = data.get('ctaProductUrl', '')

  switch (contentType) {
    case TYPE_CONTENT_SERIES:
    case TYPE_CONTENT_SERIES_GUIDE:
      return (
        <MetadataWrapper {...props}>
          <SeriesMetadata {...props} />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_VIDEO:
    case TYPE_CONTENT_EPISODE_GUIDE:
      return (
        <MetadataWrapper {...props}>
          <VideoEpisodeMetadata {...props} />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_PERSON:
      return (
        <TitleLink
          title={title}
          url={url}
          onClick={(e) => handleTileV2OnClick(e, props)}
          contentType={contentType}
        />
      )
    case TYPE_CONTENT_TAG:
      return (
        <MetadataWrapper {...props}>
          <TitleLink
            title={title}
            url={url}
            onClick={(e) => handleTileV2OnClick(e, props)}
            contentType={contentType}
          />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_PRODUCT:
      return (
        <MetadataWrapper {...props}>
          <TitleLink
            title={title}
            url={ctaProductUrl}
            onClick={(e) => handleTileV2OnClick(e, props)}
            contentType={contentType}
          />
          <p className="tile-v2__teaser">
            {data.get('teaser', '')}
          </p>
        </MetadataWrapper>
      )
    default:
      return null
  }
}

/**
 * The complete rendered metadata for a tile
 * @param {Object} props - The props
 * @returns {JSX.Element} - The metadata for a content tile
 */
function TileV2GraphqlMetadata (props) {
  return (
    <Metadata {...props} />
  )
}

function tileV2GraphqlMetadataPropsAreEqual (prevProps, nextProps) {
  const prevData = prevProps.data
  const nextData = nextProps.data
  const prevUserLanguage = prevProps.userLanguage
  const nextUserLanguage = nextProps.userLanguage
  const prevStaticText = prevProps.staticText
  const nextStaticText = nextProps.staticText

  return prevData.equals(nextData)
    && prevStaticText.equals(nextStaticText)
    && prevUserLanguage === nextUserLanguage
}

const MemoizedTileV2Metadata = React.memo(TileV2GraphqlMetadata, tileV2GraphqlMetadataPropsAreEqual)

export default MemoizedTileV2Metadata
